import { Component, CreateEffectOptions, effect, input, signal, inject } from '@angular/core';
import { ChartsModule, SeriesLabelsContentArgs } from '@progress/kendo-angular-charts';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { AnalyticsService, TopFiveData, QueryType } from 'src/app/core/services/analytics.service';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { ExcelModule, GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { CommonModule } from '@angular/common';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { EscalationOutcomesDetailsService } from './services/escalationoutcomesdetails.service';
import { environment } from 'src//environments/environment';
import { Observable } from 'rxjs';
import { DATE_FILTER_OPERATORS } from 'src/app/core/constants/filter-operators';

@Component({
  selector: 'app-outcome-status',
  standalone: true,
  imports: [ChartsModule, LoaderModule, CommonModule, DialogsModule, GridModule, ExcelModule],
  templateUrl: './outcome-status.component.html',
  styleUrl: './outcome-status.component.scss',
  providers: [AnalyticsService, EscalationOutcomesDetailsService],
})
export class OutcomeStatusComponent extends BaseComponent {
  public data = input<TopFiveData[]>([]);

  public organizationId = input<number | null>();
  public dateRange = input<string[] | null>(null);
  public truePositivePercentage: number = 0;
  public falsePositivePercentage: number = 0;
  public unknownPercentage: number = 0;
  public autofit = true;
  public isLoading = signal<boolean>(true);
  public readonly gridService = inject(EscalationOutcomesDetailsService);
  public showPopup = false;
  public startDate = input<string | null>();
  public endDate = input<string | null>();
  public dateFilterOperators = DATE_FILTER_OPERATORS;
  queryType = input<QueryType>(QueryType.All);
  selectedOrgIds = input<number[]>([]);

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(
      () => {
        const data = this.data();
        if (data) {
          this.calculatePercentages();
          this.isLoading.set(false);
        } else {
          this.isLoading.set(true);
        }
      },
      { allowSignalWrites: true } as CreateEffectOptions
    );
  }

  private calculatePercentages() {
    const total = this.data().reduce((sum, item) => sum + item.count, 0);

    if (total > 0) {
      const truePositive = this.data().find((item) => item.description === 'True Positive');
      const falsePositive = this.data().find((item) => item.description === 'False Positive');
      const unknown = this.data().find((item) => item.description === 'Unknown');

      this.truePositivePercentage = truePositive ? (truePositive.count / total) * 100 : 0;
      this.falsePositivePercentage = falsePositive ? (falsePositive.count / total) * 100 : 0;
      this.unknownPercentage = unknown ? (unknown.count / total) * 100 : 0;
    } else {
      this.truePositivePercentage = 0;
      this.falsePositivePercentage = 0;
      this.unknownPercentage = 0;
    }
  }

  public labelContent(e: SeriesLabelsContentArgs): string {
    const percentage = (e.value * 100).toFixed(0); // Convert value to percentage and format to two decimal places
    return `${e.category}: \n ${percentage}%`;
  }

  onContextMenuSelect(event: any) {
    if (event?.text === 'View Data') {

      // Determine query type and org IDs based on filter context
      const queryType = this.queryType().toLowerCase(); // 'all' or 'filtered'
      const orgIds = this.queryType() === QueryType.Filtered && this.selectedOrgIds().length > 0 
        ? this.selectedOrgIds().join(',') 
        : this.organizationId();

      const baseQueryString = `org_ids=${orgIds}&type=${queryType}`;
      const queryString = `${baseQueryString}&current_start=${this.startDate()}&current_end=${this.endDate()}`;

      this.gridService.url = `${environment.apiUrl}/v1/analytics/administrative/escalationoutcomeschartdetails(${this.organizationId()})`;
      this.gridService.appendQueryString = queryString;
      this.gridService.read();
      this.showPopup = true;
    } else {
      console.warn('Invalid event structure:', event);
    }
  }

  public allData = (): Observable<GridDataResult> => {
    return this.gridService.queryAll();
  };

  // Close popup
  close() {
    this.showPopup = false;
  }
}
